import { CaseConsultationFile, CONTENT_TYPE, IndicationFileStatus, PRESCRIPTION_INTEGRATION } from '@abiglobalhealth/go-types'
import { ReactNode, useEffect, useState } from 'react'

import { rateConsultation } from '@/api/consultations'
import { Button } from '@/components/Button'
import { Rating } from '@/components/Consultation/steps/Answer/Rating'
import { ReportCard } from '@/components/Consultation/steps/Answer/ReportCard'
import { TakeAgain } from '@/components/Consultation/steps/Answer/TakeAgain'
import { PrescriptionIcon } from '@/components/icons/consultations/PrescriptionIcon'
import { ProfessionalAssessmentIcon } from '@/components/icons/consultations/ProfessionalAssessmentIcon'
import { OpenLinkIcon } from '@/components/icons/OpenLinkIcon'
import { ReferralIcon } from '@/components/icons/ReferralIcon'
import { RxIcon } from '@/components/icons/RxIcon'
import { SickNoteIcon } from '@/components/icons/SickNoteIcon'
import { SummaryIcon } from '@/components/icons/SummaryIcon'
import { useConsultation } from '@/hooks/useConsultation'
import { useConsultationType } from '@/hooks/useConsultationType'
import { useLoading } from '@/hooks/useLoading'
import { useTagManager } from '@/hooks/useTagManager'
import { useToast } from '@/hooks/useToast'
import { useUser } from '@/hooks/useUser'
import { getSlugTranslation } from '@/locale/utils'

const REPORT_CONTENT: Record<CONTENT_TYPE, { label: string; icon: ReactNode }> = {
  [CONTENT_TYPE.CONSULTATION_NOTES]: {
    label: 'general.consultation.reportCard.itemsIncluded.consultationNotes',
    icon: <SummaryIcon customClassName="h-5 w-5" />
  },
  [CONTENT_TYPE.PRESCRIPTION]: {
    label: 'general.consultation.reportCard.itemsIncluded.prescription',
    icon: <PrescriptionIcon customClassName="h-5 w-5" />
  },
  [CONTENT_TYPE.PRESCRIPTION_DECISIONS]: {
    label: 'general.consultation.reportCard.itemsIncluded.prescriptionDecision',
    icon: <RxIcon customClassName="h-5 w-5" />
  },
  [CONTENT_TYPE.SICK_CERTIFICATE]: {
    label: 'general.consultation.reportCard.itemsIncluded.sickCertificate',
    icon: <SickNoteIcon />
  },
  [CONTENT_TYPE.REFERRAL]: {
    label: 'general.consultation.reportCard.itemsIncluded.referral',
    icon: <ReferralIcon className="h-5 w-5" />
  },
  [CONTENT_TYPE.DOCTORS_ASSESSMENT]: {
    label: 'general.consultation.reportCard.itemsIncluded.assessment',
    icon: <ProfessionalAssessmentIcon customClassName="h-5 w-5" />
  }
}

type ReportProps = { report: CaseConsultationFile & { status: IndicationFileStatus.CREATED } }

const RATING_DELAY_MS = 5000

export const Report = ({ report }: ReportProps) => {
  const { consultation, updateConsultation } = useConsultation()
  const { consultationType } = useConsultationType()
  const { showLoading, hideLoading } = useLoading()
  const { sendGTMEvent } = useTagManager()
  const { show } = useToast()
  const { user } = useUser()
  const [ratingDismissed, setRatingDismissed] = useState(false)
  const [ratingDelayElapsed, setRatingDelayElapsed] = useState(false)
  const hasRating = consultation?.rating != null
  const showRating = !hasRating && ratingDelayElapsed && !ratingDismissed
  const hasPrescriptionIntegration =
    Boolean(consultation?.prescription?.integration) && consultation?.prescription?.integration !== PRESCRIPTION_INTEGRATION.DEFAULT

  useEffect(() => {
    if (hasRating) return

    const timeoutId = setTimeout(() => setRatingDelayElapsed(true), RATING_DELAY_MS)

    return () => clearTimeout(timeoutId)
  }, [hasRating])

  const openReport = () => {
    window.open(report.url, '_blank', 'noopener,noreferrer')
    sendGTMEvent(`${consultationType}_download_pdf`)
  }

  const submitRating = async (rating: number) => {
    showLoading()

    try {
      updateConsultation(
        await rateConsultation({
          rating,
          uniqueId: user?.data?.uniqueId as string
        })
      )
      setRatingDismissed(true)
      show(getSlugTranslation('answer.thanks.rating'))
    } finally {
      hideLoading()
    }
  }

  return (
    <div className="flex flex-1 flex-col items-center justify-between gap-4 overflow-y-auto p-4" data-testid="report-screen">
      <ReportCard>
        <h2 className="text-2xl leading-9 font-bold">{getSlugTranslation('general.consultation.reportCard.reportReady')}</h2>

        <div className="flex flex-col gap-4">
          <p className="text-gray-text text-sm tracking-[0.28px]">{getSlugTranslation('general.consultation.reportCard.whatsIncluded')}</p>

          <ul className="flex flex-col gap-4">
            {report.content?.map((type) => {
              const item = REPORT_CONTENT[type]
              if (!item) return null

              return (
                <li className="flex items-center gap-2" data-testid={`report-content-${type}`} key={type}>
                  <span className="text-primary bg-primary-light grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-sm p-1 [&>svg]:h-6 [&>svg]:w-6">
                    {item.icon}
                  </span>
                  <span>{getSlugTranslation(item.label)}</span>
                </li>
              )
            })}
          </ul>

          {hasPrescriptionIntegration && !consultation?.prescription?.isRefused && (
            <p className="text-gray-text text-sm">{getSlugTranslation('general.consultation.reportCard.prescription.hint')}</p>
          )}
        </div>

        <div className="border-t-gray-background flex flex-col gap-4 border-t pt-6">
          <Button customClassName="flex items-center justify-center gap-2 text-sm" data-testid="open-report" onClick={openReport}>
            <OpenLinkIcon className="h-5 w-5" />
            {getSlugTranslation('general.consultation.openReportButton')}
          </Button>

          <p className="rounded-lg bg-gray-100 p-2 text-center text-sm">{getSlugTranslation('general.consultation.reportCard.hint')}</p>
        </div>
      </ReportCard>

      <TakeAgain closeSessionOnReset confirmBeforeReset onOpenReport={openReport} />

      {showRating && <Rating onClose={() => setRatingDismissed(true)} onSubmit={submitRating} />}
    </div>
  )
}
